import api from './api';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend.designinfra.co.in/api';

// The API base minus the trailing `/api` — images are served from
// {host}/storage/{path}, not from under the API prefix.
const STORAGE_BASE = `${API_URL.replace(/\/api\/?$/, '')}/storage`;

/** Expand a stored image path (`gallery/a1b2c3.webp`) into a URL. */
export const galleryImageUrl = (image) =>
  image ? `${STORAGE_BASE}/${image}` : null;

const toBool = (value) => Boolean(Number(value));

const unwrap = (response) => response.data?.data ?? response.data;

/**
 * API fields: id, name, slug, sort_order, is_active, images_count
 */
const normalizeCategory = (raw) => ({
  id: raw.id,
  name: raw.name ?? '',
  slug: raw.slug ?? '',
  sortOrder: Number(raw.sort_order ?? 0),
  isActive: toBool(raw.is_active),
  imagesCount: Number(raw.images_count ?? 0),
});

/**
 * API fields: id, gallery_category_id, title, location, capacity, image,
 * is_active, category { id, name, slug, is_active }
 */
const normalizeImage = (raw) => ({
  id: raw.id,
  categoryId: raw.gallery_category_id ?? null,
  categoryName: raw.category?.name ?? '',
  title: raw.title ?? '',
  // Nullable on the API; '' keeps the inputs controlled.
  location: raw.location ?? '',
  capacity: raw.capacity ?? '',
  image: raw.image ?? null,
  imageUrl: galleryImageUrl(raw.image),
  isActive: toBool(raw.is_active),
  createdAt: raw.created_at ?? null,
});

const multipart = { headers: { 'Content-Type': 'multipart/form-data' } };

export const galleryService = {
  // Every image, active or not; GalleryPage filters locally.
  getAll: async (params = {}) => {
    const response = await api.get('/gallery', { params });
    const list = unwrap(response) ?? [];
    const images = list.map(normalizeImage);
    return { data: { images, total: images.length } };
  },

  /**
   * Upload several files into one category.
   * `images[]`, not `images`: PHP keeps only the last value of a repeated key.
   */
  upload: async ({ categoryId, title, location, capacity, isActive = true, files }) => {
    const fd = new FormData();
    fd.append('gallery_category_id', categoryId ?? '');
    fd.append('title', title ?? '');
    fd.append('location', location ?? '');
    fd.append('capacity', capacity ?? '');
    fd.append('is_active', isActive ? '1' : '0');
    files.forEach((file) => fd.append('images[]', file));
    const response = await api.post('/gallery/upload', fd, multipart);
    return { data: (unwrap(response) ?? []).map(normalizeImage) };
  },

  // POST, not PUT — PHP does not parse multipart on PUT, so a new photo would be lost.
  update: async (id, { categoryId, title, location, capacity, isActive, image }) => {
    const fd = new FormData();
    fd.append('gallery_category_id', categoryId ?? '');
    fd.append('title', title ?? '');
    fd.append('location', location ?? '');
    fd.append('capacity', capacity ?? '');
    fd.append('is_active', isActive ? '1' : '0');
    // Only a real File replaces the photo; the existing one is a string URL.
    if (image instanceof File) {
      fd.append('image', image);
    }
    const response = await api.post(`/gallery/update/${id}`, fd, multipart);
    return { data: normalizeImage(unwrap(response)) };
  },

  delete: async (id) => api.delete(`/gallery/${id}`),

  toggleStatus: async (id) => api.patch(`/gallery/toggle/${id}`),

  getCategories: async () => {
    const response = await api.get('/gallery/categories');
    return { data: (unwrap(response) ?? []).map(normalizeCategory) };
  },

  createCategory: async ({ name, sortOrder, isActive = true }) => {
    const response = await api.post('/gallery/categories', {
      name,
      sort_order: Number(sortOrder) || 0,
      is_active: isActive,
    });
    return { data: normalizeCategory(unwrap(response)) };
  },

  updateCategory: async (id, { name, sortOrder, isActive }) => {
    const response = await api.put(`/gallery/categories/${id}`, {
      name,
      sort_order: Number(sortOrder) || 0,
      is_active: isActive,
    });
    return { data: normalizeCategory(unwrap(response)) };
  },

  deleteCategory: async (id) => api.delete(`/gallery/categories/${id}`),

  toggleCategoryStatus: async (id) => api.patch(`/gallery/categories/toggle/${id}`),
};

export default galleryService;
